/* eslint func-style: "off" */

import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  //actions
  FETCH_SOURCE_CHECKER_RESULTS,
  FETCH_STORY_CHECKER_RESULTS,
  FETCH_SOURCE_CHECKER_SIMILARS,
  FETCH_STORY_CHECKER_DETAIL,
  // action creators
  fetchSourceCheckerResultsSucceeded,
  fetchSourceCheckerResultsFailed,
  fetchStoryCheckerResultsSucceeded,
  fetchStoryCheckerResultsFailed,
  fetchSourceCheckerSimilarsSucceeded,
  fetchSourceCheckerSimilarsFailed,
  fetchStoryCheckerDetailSucceeded,
  fetchStoryCheckerDetailFailed
} from './StoryAndSourceChecker.ducks';
import apiClient from 'helpers/apiClient';
import split from 'lodash/split';
import map from 'lodash/map';

// NOTE: "SourceType" is the 'type of Source being checked' vs. a Source Type like 'twitter', 'blogs', etc.
const getSourceOrStoryType = (state) => state.StoryAndSourceChecker.selectedSourceOrStoryType;
const getSourcesOrStoryToCheckList = (state) => {
  const { sourcesOrStoriesToCheck } = state.StoryAndSourceChecker;
  return split(sourcesOrStoriesToCheck, '\n').reduce((array, value) => {
    if (value === '') {
      return array;
    }
    if (value.search(/\s/) === -1) {
      array.push(value);
      return array;
    }
    const trimmed = value.trim();
    if (trimmed !== '') {
      array.push(trimmed);
    }
    return array;
  }, []);
};

export const getSelectedProfiles = (state) => ( state.StoryAndSourceChecker.selectedProfiles );

const _getSourceProperties = (sourceType, sourcesOrStoriesToCheckList, fuzzy=false) => {
  if (sourceType === 'domain') {
    return {domains: sourcesOrStoriesToCheckList};
  }
  return {
    mediaOutlets: sourcesOrStoriesToCheckList,
    searchForSimilar: fuzzy
  };
};

export const getSelectedTitle = (state) => state.StoryAndSourceChecker.title;

// A shitty name for what should just be called 'sourceType', but isn't because "source type"
// means something else in story checker
export const getContentSourceType = (state) => state.StoryAndSourceChecker.selectedSource;

const bodyFromCheckList = (checkList) => checkList.join('\n');

const _getStoryProperties = (sourceType, storiesToCheckList, title='', profiles = []) => {
  if (sourceType === 'urls') {
    return {
      profileIds: map(profiles, '_id'),
      urls: storiesToCheckList
    };
  }
  return {
    profileIds: map(profiles, '_id'),
    body: bodyFromCheckList(storiesToCheckList),
    title
  };
};

const _getApiProperties = (campaignId, sourceType, sourcesOrStoriesToCheckList, fuzzy=false) => {
  const sourceTypeUrl = {
    domain: 'domains',
    mediaOutlet: 'media-outlets',
  };

  return {
    path: `/api/analytics/source-checker/${sourceTypeUrl[sourceType]}`,

    bodyParams: {
      campaignId,
      ..._getSourceProperties(sourceType, sourcesOrStoriesToCheckList, fuzzy)
    }
  };
};

// eslint-disable-next-line max-params
const _getStoryApiProperties = (
  campaignId, sourceType, sourcesOrStoriesToCheckList, contentSourceType, title='', profiles = []
) => {
  const sourceTypeUrl = {
    urls: 'urls',
    content: 'content',
  };

  return {
    path: `/api/analytics/story-checker/${sourceTypeUrl[sourceType]}`,
    bodyParams: {
      campaignId,
      sourceType: contentSourceType,
      ..._getStoryProperties(sourceType, sourcesOrStoriesToCheckList, title, profiles)
    }
  };
};

export const fetchSourceCheckerResults = function* (action) {
  try {
    const { campaignId } = action;
    const sourceType = yield select(getSourceOrStoryType);
    const sourcesOrStoriesToCheckList = yield select(getSourcesOrStoryToCheckList);
    const apiProperties = _getApiProperties(campaignId, sourceType, sourcesOrStoriesToCheckList);
    const payload = yield call(apiClient, apiProperties.path, {
      method: 'POST',
      body: JSON.stringify(apiProperties.bodyParams)
    });
    yield put(fetchSourceCheckerResultsSucceeded(payload));
  } catch (e) {
    yield put(fetchSourceCheckerResultsFailed());
  }
};

export const fetchStoryCheckerResults = function* (action) {
  try {
    const { campaignId } = action;
    const sourceType = yield select(getSourceOrStoryType);
    const sourcesOrStoriesToCheckList = yield select(getSourcesOrStoryToCheckList);
    const contentSourceType = yield select(getContentSourceType);
    const profiles = yield select(getSelectedProfiles);
    const title = yield select(getSelectedTitle);
    const apiProperties = _getStoryApiProperties(
      campaignId, sourceType, sourcesOrStoriesToCheckList, contentSourceType, title, profiles)
    ;
    const payload = yield call(apiClient, apiProperties.path, {
      method: 'POST',
      body: JSON.stringify(apiProperties.bodyParams)
    });
    // Body and title are required for story checker detail highlighting
    if (sourceType === 'content') {
      payload[0].body = bodyFromCheckList(sourcesOrStoriesToCheckList);
      payload[0].title = title;
    }
    yield put(fetchStoryCheckerResultsSucceeded(payload));
  } catch (e) {
    yield put(fetchStoryCheckerResultsFailed());
  }
};

export const fetchSourceCheckerSimilars = function* (action) {
  try {
    const { campaignId, mediaOutlet } = action.payload;
    const sourceType = yield select(getSourceOrStoryType);
    const apiProperties = _getApiProperties(campaignId, sourceType, [ mediaOutlet ], true);
    const similarSources = yield call(apiClient, apiProperties.path, {
      method: 'POST',
      body: JSON.stringify(apiProperties.bodyParams)
    });
    yield put(fetchSourceCheckerSimilarsSucceeded({ mediaOutlet, similarSources }));
  } catch (e) {
    yield put(fetchSourceCheckerSimilarsFailed());
  }
};

export const fetchStoryCheckerDetail = function* (action) {
  try {
    const result = yield call(apiClient, '/api/analytics/story-checker/detail', {
      method: 'POST',
      body: JSON.stringify(action.payload)
    });
    yield put(fetchStoryCheckerDetailSucceeded(result));
  } catch (e) {
    yield put(fetchStoryCheckerDetailFailed());
  }
};

export const fetchSourceCheckerResultsSaga = function* () {
  yield takeLatest( FETCH_SOURCE_CHECKER_RESULTS, fetchSourceCheckerResults);
};

export const fetchStoryCheckerResultsSaga = function* () {
  yield takeLatest( FETCH_STORY_CHECKER_RESULTS, fetchStoryCheckerResults);
};

export const fetchStoryCheckerDetailSaga = function* () {
  yield takeLatest( FETCH_STORY_CHECKER_DETAIL, fetchStoryCheckerDetail);
};

export const fetchSourceCheckerSimilarsSaga = function* () {
  yield takeLatest( FETCH_SOURCE_CHECKER_SIMILARS, fetchSourceCheckerSimilars);
};

const sagas = function* () {
  yield all([
    fork(fetchSourceCheckerResultsSaga),
    fork(fetchSourceCheckerSimilarsSaga),
    fork(fetchStoryCheckerResultsSaga),
    fork(fetchStoryCheckerDetailSaga),
  ]);
};

export default sagas;
export {
  // protected
  _getApiProperties,
  _getSourceProperties,
  // public
  getSourcesOrStoryToCheckList,
  getSourceOrStoryType
};
